import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import AuthModal from '../components/AuthModal';
import ShareButton from '../components/ShareButton';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, MessageCircle, ShoppingBag, ShoppingCart, MapPin, Droplets, ChevronLeft, ChevronRight, Heart, Flag, Loader2, Pencil, Trash2, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price, currency = 'IDR') => {
  if (currency === 'IDR') return `Rp ${Number(price).toLocaleString('id-ID')}`;
  return `${currency} ${Number(price).toLocaleString()}`;
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, fetchCartCount } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [wishlisted, setWishlisted] = useState(false);
  const [reportDialog, setReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reporting, setReporting] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    const fetchListing = () => axios.get(`${API}/listings/${id}`)
      .then(r => setListing(r.data))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
    fetchListing();
    const interval = setInterval(fetchListing, 20000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchListing(); };
    const onFocus = () => fetchListing();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [id, navigate]);

  // After Google login redirect back, execute pending action
  useEffect(() => {
    if (isAuthenticated && listing) {
      const saved = sessionStorage.getItem('pending_listing_action');
      if (saved) {
        const { listingId, action } = JSON.parse(saved);
        sessionStorage.removeItem('pending_listing_action');
        if (listingId === id) {
          if (action === 'buy_now') {
            doAddToCartAndGo();
          } else if (action === 'add_to_cart') {
            doAddToCart();
          }
        }
      }
    }
  }, [isAuthenticated, listing, id]);

  useEffect(() => {
    if (isAuthenticated && listing) {
      axios.get(`${API}/wishlist/check/${id}`)
        .then(r => setWishlisted(r.data.wishlisted))
        .catch(() => {});
    }
  }, [isAuthenticated, id, listing]);

  // Helper that handles the 409 single_seller_cart conflict warmly:
  //   • OK   → replace cart with this item (cart_replace_with) + go to /cart if requested
  //   • Cancel → silently tuck this listing into wishlist + reassuring toast
  // No "you have to choose" guilt, just "we saved it for later" framing.
  const addToCartWithReplaceFlow = async (proceedNavigate) => {
    try {
      await axios.post(`${API}/cart/add`, { listing_id: id, quantity: 1 });
      fetchCartCount();
      toast.success('Added to cart!');
      if (proceedNavigate) navigate('/cart');
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'single_seller_cart') {
        const sellerName = detail.current_seller_name || 'another seller';
        const productName = listing?.product_name || 'this item';
        const ok = window.confirm(
          `You're already shopping with ${sellerName}.\n\n` +
          `• Tap OK to replace your cart with ${productName}.\n` +
          `• Tap Cancel and we'll tuck it into your wishlist for after your ${sellerName} order.`
        );
        if (!ok) {
          // Soft landing: save to wishlist (idempotent) so the buyer doesn't
          // lose the discovery. Toast frames it as "saved", not "rejected".
          try {
            const { data: check } = await axios.get(`${API}/wishlist/check/${id}`);
            if (!check?.wishlisted) {
              await axios.post(`${API}/wishlist/${id}`, {});
              toast.success(`Tucked into your wishlist 💝 Finish your order with ${sellerName} first — we'll keep ${productName} safe for you.`);
            } else {
              toast.success(`Already in your wishlist 💝 Finish your order with ${sellerName} first — we'll be here when you're back.`);
            }
          } catch {
            // Wishlist save failed? Don't block, don't alarm — just acknowledge.
            toast(`Your ${sellerName} cart is safe. Come back for ${productName} after checkout!`);
          }
          return false;
        }
        try {
          await axios.post(`${API}/cart/replace-with`, { listing_id: id, quantity: 1 });
          fetchCartCount();
          toast.success('Cart replaced — ready to check out with this item.');
          if (proceedNavigate) navigate('/cart');
          return true;
        } catch (err2) {
          toast.error(err2.response?.data?.detail || 'Failed to replace cart');
          return false;
        }
      }
      toast.error(typeof detail === 'string' ? detail : 'Failed to add to cart');
      return false;
    }
  };

  const doAddToCart = async () => {
    setAddingToCart(true);
    try { await addToCartWithReplaceFlow(false); }
    finally { setAddingToCart(false); }
  };

  const doAddToCartAndGo = async () => {
    setBuyingNow(true);
    try { await addToCartWithReplaceFlow(true); }
    finally { setBuyingNow(false); }
  };

  const handleWishlist = async () => {
    if (!isAuthenticated) { setShowAuth(true); return; }
    try {
      const res = await axios.post(`${API}/wishlist/${id}`, {});
      setWishlisted(res.data.wishlisted);
      toast.success(res.data.message);
    } catch {
      toast.error('Failed to update wishlist');
    }
  };

  const handleReport = async () => {
    if (!reportReason) { toast.error('Please select a reason'); return; }
    setReporting(true);
    try {
      const res = await axios.post(`${API}/reports/${id}`, { reason: reportReason, details: reportDetails });
      toast.success(res.data.message);
      setReportDialog(false);
      setReportReason('');
      setReportDetails('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setReporting(false);
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      setPendingAction('add_to_cart');
      sessionStorage.setItem('pending_listing_action', JSON.stringify({ listingId: id, action: 'add_to_cart' }));
      setShowAuth(true);
      return;
    }
    doAddToCart();
  };

  const [buyingNow, setBuyingNow] = useState(false);

  const handleBuyNow = async () => {
    if (!isAuthenticated) {
      setPendingAction('buy_now');
      sessionStorage.setItem('pending_listing_action', JSON.stringify({ listingId: id, action: 'buy_now' }));
      setShowAuth(true);
      return;
    }
    doAddToCartAndGo();
  };

  const [duplicating, setDuplicating] = useState(false);
  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const res = await axios.post(`${API}/listings/${id}/duplicate`);
      toast.success('Listing duplicated! Redirecting to edit...');
      navigate(`/listing/${res.data.listing_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate listing');
    } finally {
      setDuplicating(false);
    }
  };

  const handleAuthSuccess = () => {
    if (pendingAction === 'buy_now') {
      doAddToCartAndGo();
    } else if (pendingAction === 'add_to_cart') {
      doAddToCart();
    }
    setPendingAction(null);
    sessionStorage.removeItem('pending_listing_action');
  };

  const handleMessage = async () => {
    if (!isAuthenticated) { setShowAuth(true); return; }
    try {
      const res = await axios.post(`${API}/messages/conversations`, {
        type: 'listing',
        listing_id: id,
        recipient_id: listing.user_id,
      });
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start conversation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFAF8]">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-8 animate-pulse">
          <div className="h-8 bg-cream-100 rounded w-32 mb-6" />
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-square bg-cream-100 rounded-2xl" />
            <div className="space-y-4"><div className="h-4 bg-cream-100 rounded w-1/4" /><div className="h-8 bg-cream-100 rounded w-3/4" /><div className="h-6 bg-cream-100 rounded w-1/3" /></div>
          </div>
        </div>
      </div>
    );
  }
  if (!listing) return null;

  const { photos, reference_photo, brand, product_name, description, price, currency, condition, remaining_amount, expiry_date, opening_date, pao_months, selling_reason, selling_reason_text, seller, quantity, sold_out, size_value, size_unit } = listing;

  const allPhotos = [...(photos || []), ...(reference_photo ? [reference_photo] : [])];
  const referenceIndex = reference_photo ? allPhotos.length - 1 : -1;
  const isSoldOut = sold_out || false;

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-body text-sm text-[#78716C] hover:text-[#2C2A29] mb-6 transition-colors" data-testid="back-btn">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <div data-testid="listing-images">
            <div className="relative overflow-hidden rounded-2xl bg-cream-50 aspect-square">
              <img src={allPhotos?.[currentImage] || 'https://via.placeholder.com/500'} alt={product_name} className={`w-full h-full object-cover ${isSoldOut && !listing.is_demo ? 'opacity-50 grayscale' : ''}`} />
              {currentImage === referenceIndex && (
                <div className="absolute top-3 left-3 bg-blue-600/85 backdrop-blur-sm px-3 py-1 rounded-full" data-testid="reference-image-badge">
                  <span className="text-xs font-body font-semibold text-white">Reference Image</span>
                </div>
              )}
              {listing.is_demo ? (
                <div className="absolute inset-0 flex items-end justify-center pb-6" data-testid="listing-sample-badge">
                  <span className="bg-amber-600/85 backdrop-blur-sm text-white px-6 py-2 rounded-full text-sm font-body font-semibold tracking-wide">
                    Sample listing
                  </span>
                </div>
              ) : isSoldOut && (
                <div className="absolute inset-0 flex items-center justify-center" data-testid="listing-sold-out-badge">
                  <span className="bg-[#2C2A29]/80 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-body font-semibold tracking-wide">
                    Sold out!
                  </span>
                </div>
              )}
              {allPhotos?.length > 1 && (
                <>
                  <button onClick={() => setCurrentImage(i => Math.max(0, i - 1))} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full p-2 shadow-sm hover:bg-white transition-all"><ChevronLeft size={20} /></button>
                  <button onClick={() => setCurrentImage(i => Math.min(allPhotos.length - 1, i + 1))} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm rounded-full p-2 shadow-sm hover:bg-white transition-all"><ChevronRight size={20} /></button>
                </>
              )}
            </div>
            {allPhotos?.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto hide-scrollbar">
                {allPhotos.map((photo, i) => (
                  <button key={i} onClick={() => setCurrentImage(i)}
                    className={`relative flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${i === currentImage ? 'border-[#F4C2C2]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                    <img src={photo} alt="" className="w-full h-full object-cover" />
                    {i === referenceIndex && (
                      <div className="absolute inset-x-0 bottom-0 bg-blue-600/80 py-0.5 text-center">
                        <span className="text-[7px] font-body font-bold text-white uppercase">Ref</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-5" data-testid="listing-details">
            <div>
              <p className="text-xs font-body uppercase tracking-[0.2em] font-semibold text-[#A89F91] mb-2">{brand}</p>
              <h1 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] mb-3">{product_name}</h1>
              <p className="font-heading text-3xl font-semibold text-[#2C2A29]">{formatPrice(price, currency)}</p>
              {/* Seller-only payout preview — only the listing owner sees it.
                  Buyers don't see any platform-fee info on this page.
                  Note: listing owner is stored as `user_id` (not seller_id —
                  seller_id only exists on orders). */}
              {!listing.is_demo && user?.user_id === listing.user_id && (() => {
                const p = Number(price) || 0;
                if (p <= 0) return null;
                const fee = Math.round(p * 0.05);
                const net = p - fee;
                return (
                  <p className="text-xs font-body text-[#78716C] mt-1.5" data-testid="seller-payout-preview">
                    You'll receive <span className="font-semibold text-emerald-700">Rp {net.toLocaleString('id-ID')}</span>{' '}
                    <span className="text-[#A89F91]">(after 5% platform fee · Rp {fee.toLocaleString('id-ID')})</span>
                  </p>
                );
              })()}
              {!listing.is_demo && <p className="text-sm font-body text-[#78716C] mt-2" data-testid="listing-quantity">Quantity: {quantity || 1}</p>}
            </div>

            {description && (
              <p className="font-body text-sm text-[#2C2A29] leading-relaxed" data-testid="listing-description">{description}</p>
            )}

            <div className="flex flex-wrap gap-2">
              {listing.barcode_verified && (
                <Badge className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-semibold border border-emerald-100 flex items-center gap-1" data-testid="barcode-verified-badge">
                  <CheckCircle2 size={12} /> Barcode Scanned
                </Badge>
              )}
              <Badge className="bg-cream-50 text-[#78716C] px-3 py-1.5 rounded-full text-xs font-medium border border-cream-100">{condition}</Badge>
              {remaining_amount && remaining_amount !== condition && (
                <Badge className="bg-cream-50 text-[#78716C] px-3 py-1.5 rounded-full text-xs font-medium border border-cream-100">{remaining_amount}</Badge>
              )}
              {size_value && size_unit && (
                <Badge className="bg-cream-50 text-[#78716C] px-3 py-1.5 rounded-full text-xs font-medium border border-cream-100" data-testid="listing-size">{size_value} {size_unit}</Badge>
              )}
              {expiry_date && <Badge className="bg-cream-50 text-[#78716C] px-3 py-1.5 rounded-full text-xs font-medium border border-cream-100">Exp: {expiry_date}</Badge>}
              {opening_date && (() => {
                let label = opening_date;
                try {
                  const d = new Date(opening_date + 'T00:00:00');
                  label = d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
                } catch { /* keep raw */ }
                return (
                  <Badge className="bg-cream-50 text-[#78716C] px-3 py-1.5 rounded-full text-xs font-medium border border-cream-100" data-testid="listing-opened">Opened: {label}</Badge>
                );
              })()}
              {pao_months && (
                <Badge className="bg-cream-50 text-[#78716C] px-3 py-1.5 rounded-full text-xs font-medium border border-cream-100" data-testid="listing-pao">PAO: {pao_months} months</Badge>
              )}
            </div>

            {selling_reason && (
              <div className="bg-[#FFF5F5] rounded-2xl p-4">
                <p className="text-xs font-body uppercase tracking-[0.15em] font-semibold text-[#A89F91] mb-1">Why selling</p>
                <p className="font-body text-sm text-[#2C2A29]">{selling_reason}{selling_reason_text ? ` — ${selling_reason_text}` : ''}</p>
              </div>
            )}

            {/* Owner view */}
            {isAuthenticated && user && listing.user_id === user.user_id ? (
              <div className="space-y-3 pt-2">
                <div className={`rounded-2xl p-4 border ${sold_out ? 'bg-cream-50 border-cream-100' : 'bg-amber-50 border-amber-200'}`}>
                  <p className={`font-body text-sm font-medium ${sold_out ? 'text-[#78716C]' : 'text-amber-800'}`} data-testid="own-listing-notice">
                    {sold_out ? 'This listing has been sold. It is preserved as a historical record.' : 'This is your listing'}
                  </p>
                </div>
                {sold_out ? (
                  <button onClick={handleDuplicate} disabled={duplicating}
                    className="w-full bg-[#2C2A29] text-white rounded-full px-6 py-3.5 font-body font-semibold transition-all hover:bg-[#1a1918] hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="duplicate-listing-btn">
                    <Copy size={18} /> {duplicating ? 'Duplicating...' : 'Duplicate as New Listing'}
                  </button>
                ) : (
                  <>
                    <button onClick={() => navigate(`/listing/${id}/edit`)}
                      className="w-full bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-3.5 font-body font-semibold transition-all hover:bg-[#ECAEAE] hover:shadow-md flex items-center justify-center gap-2"
                      data-testid="edit-listing-btn">
                      <Pencil size={18} /> Edit Listing
                    </button>
                    <ShareButton
                      url={typeof window !== 'undefined' ? `${window.location.origin}/api/s/${id}` : ''}
                      title={`${brand ? brand + ' — ' : ''}${product_name} · {Beauty} Stashmart`}
                      text={`I just listed ${brand ? brand + ' ' : ''}${product_name} on {Beauty} Stashmart — take a look:`}
                      label="Share this listing"
                      testid="share-listing-btn"
                    />
                    <button onClick={async () => {
                        if (!window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) return;
                        try {
                          await axios.delete(`${API}/listings/${id}`);
                          toast.success('Listing deleted');
                          navigate('/');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to delete listing');
                        }
                      }}
                      className="w-full border border-red-200 text-red-600 rounded-full px-6 py-3 font-body font-semibold transition-all hover:bg-red-50 hover:border-red-300 flex items-center justify-center gap-2"
                      data-testid="delete-listing-btn">
                      <Trash2 size={18} /> Delete Listing
                    </button>
                  </>
                )}
              </div>
            ) : listing.is_demo ? (
              <div className="space-y-3 pt-2">
                <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200" data-testid="sample-listing-notice">
                  <p className="font-body text-sm text-amber-800 font-semibold">Sample listing</p>
                  <p className="font-body text-xs text-amber-700 mt-1">This is a sample listing for preview purposes. It cannot be purchased.</p>
                </div>
              </div>
            ) : (
              <>
                {/* Seller Info */}
                {seller && (
                  <div className="bg-white rounded-2xl p-4 border border-cream-100/50 cursor-pointer hover:shadow-sm transition-shadow"
                    onClick={() => navigate(`/profile/${seller.user_id}`)} data-testid="seller-info">
                    <p className="text-xs font-body uppercase tracking-[0.15em] font-semibold text-[#A89F91] mb-3">Sold by</p>
                    <div className="flex items-center gap-3">
                      {seller.picture ? (
                        <img src={seller.picture} alt={seller.name} className="w-12 h-12 rounded-full object-cover border-2 border-blush-200" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blush-200 flex items-center justify-center text-lg font-bold text-[#2C2A29]">{seller.name?.[0]}</div>
                      )}
                      <div>
                        <p className="font-body font-semibold text-[#2C2A29]">{seller.name}</p>
                        <div className="flex items-center gap-2 text-xs font-body text-[#78716C]">
                          {seller.skin_type && <span><Droplets size={10} className="inline mr-1" />{seller.skin_type} skin</span>}
                          {seller.city && <span><MapPin size={10} className="inline mr-1" />{seller.city}</span>}
                        </div>
                      </div>
                    </div>
                    {seller.skin_concerns?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {seller.skin_concerns.map(c => (
                          <span key={c} className="bg-[#FFF5F5] text-[#F4C2C2] px-2 py-0.5 rounded-full text-xs font-medium">{c}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  {isSoldOut ? (
                    <div className="flex-1 text-center bg-cream-50 text-[#78716C] rounded-full px-5 py-3.5 font-body font-semibold border border-cream-100" data-testid="sold-out-notice">
                      This item is sold out
                    </div>
                  ) : (
                    <>
                      <button onClick={handleAddToCart} disabled={addingToCart}
                        className="flex-1 border border-cream-100 bg-transparent text-[#2C2A29] rounded-full px-5 py-3.5 font-body font-semibold transition-all hover:border-[#F4C2C2] hover:bg-[#FFF5F5] disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="add-to-cart-btn">
                        <ShoppingCart size={18} />
                        {addingToCart ? 'Adding...' : 'Add to Cart'}
                      </button>
                      <button onClick={handleBuyNow} disabled={buyingNow}
                        className="flex-1 bg-[#F4C2C2] text-[#2C2A29] rounded-full px-5 py-3.5 font-body font-semibold transition-all hover:bg-[#ECAEAE] hover:shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="buy-now-btn">
                        {buyingNow ? <><Loader2 size={18} className="animate-spin" /> Processing...</> : <><ShoppingBag size={18} /> Buy Now</>}
                      </button>
                    </>
                  )}
                </div>
                {!isSoldOut && (
                  <button onClick={handleMessage}
                    className="w-full border border-cream-100 bg-transparent text-[#2C2A29] rounded-full px-6 py-3 font-body font-semibold transition-all hover:border-[#F4C2C2] hover:bg-[#FFF5F5] flex items-center justify-center gap-2"
                    data-testid="message-seller-btn">
                    <MessageCircle size={18} /> Ask Seller
                  </button>
                )}
                <div className="flex gap-3">
                  <button onClick={handleWishlist}
                    className={`flex-1 rounded-full px-6 py-3 font-body font-semibold transition-all flex items-center justify-center gap-2 ${
                      wishlisted
                        ? 'bg-[#FFF5F5] text-[#E05A5A] border border-[#F4C2C2]'
                        : 'border border-cream-100 bg-transparent text-[#2C2A29] hover:border-[#F4C2C2] hover:bg-[#FFF5F5]'
                    }`}
                    data-testid="wishlist-detail-btn">
                    <Heart size={18} className={wishlisted ? 'fill-current' : ''} />
                    {wishlisted ? 'Wishlisted' : 'Add to Wishlist'}
                  </button>
                  <ShareButton
                    url={typeof window !== 'undefined' ? `${window.location.origin}/api/s/${id}` : ''}
                    title={`${brand ? brand + ' — ' : ''}${product_name} · {Beauty} Stashmart`}
                    text={`Check out this beauty find on {Beauty} Stashmart: ${brand ? brand + ' ' : ''}${product_name}`}
                    label="Share"
                    testid="share-listing-btn"
                    className="flex-1"
                  />
                </div>
                <button onClick={() => { if (!isAuthenticated) { setShowAuth(true); return; } setReportDialog(true); }}
                  className={`w-full rounded-full px-6 py-2.5 font-body text-sm text-[#A89F91] transition-all hover:text-red-500 hover:bg-red-50 flex items-center justify-center gap-2 ${isSoldOut ? 'hidden' : ''}`}
                  data-testid="report-listing-btn">
                  <Flag size={14} /> Report Listing
                </button>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Report Dialog */}
      <Dialog open={reportDialog} onOpenChange={setReportDialog}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Report Listing</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Help us keep the marketplace safe. Select a reason below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {['Fake product', 'Inappropriate content', 'Scam/fraud', 'Wrong category', 'Expired product', 'Other'].map(r => (
              <button key={r} onClick={() => setReportReason(r)}
                className={`w-full text-left px-4 py-2.5 rounded-xl font-body text-sm transition-all ${
                  reportReason === r
                    ? 'bg-red-50 text-red-600 border border-red-200 font-medium'
                    : 'bg-cream-50 text-[#2C2A29] border border-cream-100 hover:border-red-200'
                }`}
                data-testid={`report-reason-${r.toLowerCase().replace(/\s+/g, '-')}`}>
                {r}
              </button>
            ))}
            <textarea
              value={reportDetails}
              onChange={e => setReportDetails(e.target.value)}
              placeholder="Additional details (optional)..."
              className="w-full bg-cream-50 border border-cream-100 rounded-xl px-4 py-3 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-transparent resize-none"
              rows={2}
              data-testid="report-details-input"
            />
            <div className="flex gap-3 pt-1">
              <button onClick={() => { setReportDialog(false); setReportReason(''); setReportDetails(''); }}
                className="flex-1 border border-cream-100 text-[#2C2A29] rounded-full px-4 py-2.5 font-body font-semibold hover:bg-cream-50 transition-all">Cancel</button>
              <button onClick={handleReport} disabled={reporting || !reportReason}
                className="flex-1 bg-red-500 text-white rounded-full px-4 py-2.5 font-body font-semibold hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="report-submit-btn">
                <Flag size={14} /> {reporting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AuthModal open={showAuth} onOpenChange={setShowAuth} onSuccess={handleAuthSuccess} />
    </div>
  );
}
